/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import apiClient from '../../../services/api-client';
import { RootState } from '../../../store/rootReducer';
import { SeedOrderDefaultValue, SeedOrdersInfo } from '../types/SeedOrderFormType';
import { CartItem } from '../../cart/storage/cart.types';
import SeedCartSlice from './seedCart.slice';
import { SeedErrorCode } from './types';

export const syncSeedCart = createAsyncThunk<
  SeedOrdersInfo | undefined,
  { seedOrder: SeedOrdersInfo; accountNumber: string }
>('seedCart/syncSeedCart', async ({ seedOrder, accountNumber }, { dispatch, rejectWithValue }) => {
  try {
    const result = await apiClient.post('/seed-orders/sync', { ...seedOrder, accountNumber });

    if (result.status === 200) return result.data as SeedOrdersInfo;
  } catch (error) {
    // const _error
    const _error = error as AxiosError<{ type: string }>;
    if (_error.response?.data.type === SeedErrorCode.InvalidOrder) {
      if (seedOrder.id) {
        dispatch(getSeedCartById({ accountNumber, id: seedOrder.id }));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = (error as AxiosError).response?.data;
      if (response?.type) {
        dispatch(SeedCartSlice.actions.setError({ accountNumber, message: response?.type }));
        throw response?.type;
      }
    }

    rejectWithValue(error);
  }
});

export const getSeedCartById = createAsyncThunk<
  SeedOrdersInfo | undefined,
  { accountNumber: string; id: string }
>('seedCart/getSeedCartById', async ({ accountNumber, id }) => {
  const result = await apiClient.get<SeedOrdersInfo, AxiosError>(
    `/seed-orders/${id}?accountNumber=${accountNumber}`,
  );
  if (result.data) {
    return result.data;
  }
  return undefined;
});

export const getSeedCart = createAsyncThunk<SeedOrdersInfo | undefined, { accountNumber: string }>(
  'seedCart/getSeedCart',
  async ({ accountNumber }) => {
    const result = await apiClient.get<SeedOrdersInfo[], AxiosError>(
      `/seed-orders?status=draft&accountNumber=${accountNumber}`,
    );
    if (result.data.length > 0) {
      return result.data[0];
    }
    return { ...SeedOrderDefaultValue, accountNumber };
  },
);

export const editPrice = createAsyncThunk<
  void,
  {
    item: CartItem;
    newPrice: number;
    newQuantity: number;
    accountNumber: string;
    editedPriceReasonCode?: string;
    editedPriceReasonNote?: string;
  }
>(
  'seedCart/editPrice',
  async (
    { accountNumber, item, newPrice, newQuantity, editedPriceReasonCode, editedPriceReasonNote },
    { getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const seedOrder = state.seedCart.seedOrder?.[accountNumber];
    const newSeedOrder = JSON.parse(JSON.stringify(seedOrder)) as SeedOrdersInfo;
    if (newSeedOrder) {
      newSeedOrder.mixes.forEach((mix) => {
        const foundProduct = mix.products.find((p) => {
          return p.lineId === item.lineId;
        });

        if (foundProduct) {
          foundProduct.editedPriceExcGst = newPrice;
          foundProduct.quantity = newQuantity;
          foundProduct.lastEditedReasonCode = editedPriceReasonCode;
          foundProduct.lastEditedReasonNote = editedPriceReasonNote;
        }
      });
      dispatch(syncSeedCart({ seedOrder: newSeedOrder, accountNumber }));
    }
  },
);

export const revertPrice = createAsyncThunk<void, { item: CartItem; accountNumber: string }>(
  'seedCart/editPrice',
  async ({ accountNumber, item }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const seedOrder = state.seedCart.seedOrder?.[accountNumber];
    const newSeedOrder = JSON.parse(JSON.stringify(seedOrder)) as SeedOrdersInfo;
    if (newSeedOrder) {
      newSeedOrder.mixes.forEach((mix) => {
        const foundProduct = mix.products.find((p) => {
          return p.lineId === item.lineId;
        });

        if (foundProduct) {
          delete foundProduct.editedPriceExcGst;
          delete foundProduct.lastEditedReasonCode;
          delete foundProduct.lastEditedReasonNote;
        }
      });
      dispatch(syncSeedCart({ seedOrder: newSeedOrder, accountNumber }));
    }
  },
);
